import {
    Pageable,
    CompanyRegistrationDto,
    BaseTestAreaDto,
    GenericTestAreaDto,
    TestStepValueWrapper,
    TaskDto,
} from '../../src/api';
import { ThirdPartyContractFileType } from '../../src/components/admin/client/manageCompanyRegistration/components/ThirdPartyContractFileType';
import { ProcessDetailCheckStepFileType } from '../../src/components/admin/products/context/ProcessDetailContext';
import { OilTestingEngineTestsFileTypes } from '../../src/components/admin/testAggregates/oilTestingEngineTests/enums/typesAndEnums';
import { EngineOilTestFieldQueryParameter } from '../../src/components/client/userMenu/product/context/FormulationContext';
import { FormulationFileTypes, PublicFiles } from '../../src/utils/enums';
import { ProductApprovalCheckStepFileType } from '../../src/components/client/userMenu/product/enums/ProductApprovalCheckStepFileType';
import { endpointConfig } from '../../src/configuration/configuration';
import { InvoiceFileType } from './adminApi/useAdminInvoiceApi';
import { ProductApiFilters, ProductFormulationApiFilters } from '../../api/api/apiUrlInterfaces';

export type ApiUrl = string;

const registrationsPrefix = 'company-registrations';
const infolettersPrefix = 'info-letters';
const checksPrefix = 'checks';
const costs = 'costs';
const salesAreas = 'sales-areas';
const sheets = 'sheets';
const engineOilTests = 'engine-oil-tests';
const operatingFluidTypes = 'operating-fluid-types';
const saeClasses = 'sae-classes';
const mbSpecifications = 'mb-specifications';
const qualityLines = 'quality-lines';
const workingMaterials = 'working-materials';
const baseOils = 'base-oils';
const additives = 'additives';
const latestHistoryChangesAccepted = 'latest_history_changes_accepted';
const news = 'news';
const testAreas = 'test-areas';
const sheetIdParam = 'sheetId';
const operatingFluidTypeIdParam = 'operatingFluidTypeId';
const letterFromMbAvailableParam = 'letterFromMbAvailable';
const mbSpecificationIdParam = 'mbSpecificationId';
const testGroups = 'test-groups';
const testSteps = 'test-steps';
const parameters = 'parameters';
const engineTypes = 'engine-types';
const fuels = 'fuels';
const invoiceAmount = 'invoice-amounts';
const testBenchResults = 'test-bench-results';
const programTestBenches = 'program-test-benches';
const serviceAreas = 'service-areas';
const frequentlyAskedQuestions = 'frequently-asked-questions';
const dblCategories = 'dbl-categories';
const dblSpecifications = 'dbl-specifications';
const thirdParties = 'third-party-contracts';
const containerSize = 'container-sizes';
const vehicles = 'vehicles';
const invoices = 'invoices';
const company = 'company';
const appendTestTree = 'appendTestStructureTree';
const products = 'products';
const formulations = 'formulations';
const testAreasType = 'testAreaType';
const workingMaterialTemplates = 'working-material-templates';
const productApprovals = 'product-approvals';
const checkSteps = 'check-steps';
const manufacturerId = 'manufacturerId';
const checkStepFileTypeQuery = 'checkStepFileType';
const companyPresentationPrefix = 'company-presentations';
const userInfoPrefix = 'user-info';
const contactPrefix = 'contact';
const testValues = 'test-values';
const testGroupIdParam = 'testGroupId';
const letterNumberParam = 'letterNumber';
const processTypeIdentifier = 'processTypeIdentifier';
const productTypeIdentifier = 'productTypeIdentifier';
const approvals = 'approvals';
const tradeAuthorization = 'tradeAuthorization';
const onlyFullFIlled = 'onlyFullFilled';
const testProgress = 'test-progress';
const fields = 'fields';
const invoiceStatesParam = 'invoiceState';
const publicFiles = 'public-files';
const legacyCompanyParam = 'legacyCompany';
const onlyTechnicallyApprovedParam = 'onlyTechnicallyApproved';
const onlyActiveParam = 'onlyActive';
const onlyApprovedParam = 'onlyApproved';
const substanceTypesParam = 'substance-types';
const categoryParam = 'category';

const maxPaginationSettingString = 'page=0&size=2000';

/**
 * CN joke service API url type
 *
 * @export
 * @interface ApiUrlTask
 */
export interface ApiUrlTask {
    userInfo: ApiUrl;
    adminInfo: ApiUrl;
    registrations: (states?: CompanyRegistrationDto.registrationState[], search?: string) => ApiUrl;
    pagableToChangeRegistrations: (pagination?: Pageable) => ApiUrl;
    pagableToChangeRegistrationsFilterByInfoLetterState: (infoLetterState: string) => ApiUrl;
    registrationFileById: (registrationId: string) => ApiUrl;
    checkFilesById: (stepId: string, id: string) => ApiUrl;
    getCheckStepFileById: (stepId: string, id: string) => ApiUrl;
    checksById: (id: string) => ApiUrl;
    registrationById: (registrationId: string) => ApiUrl;
    updateSalutation: () => ApiUrl;
    registrationsCheckById: (registrationId: string) => ApiUrl;
    registrationsCheckByIdStepById: (registrationId: string, stepId: string) => ApiUrl;
    deleteFileByIdForRegistrationById: (registrationId: string, presentationId: string) => ApiUrl;
    downloadFileByIdForRegistrationById: (registrationId: string, presentationId: string) => ApiUrl;
    costs: ApiUrl;
    getCosts: () => ApiUrl;
    uploadNewCost: () => ApiUrl;
    costById: (costId: string) => ApiUrl;
    deleteCost: (costId: string) => ApiUrl;

    sheets: ApiUrl;
    getSheets: () => ApiUrl;
    getSheetsWithSheetType: (sheetType: string) => ApiUrl;
    getSheetsWithSheetTypeAndSearch: (sheetType: string, search?: string) => ApiUrl;
    getSheetsWithSheetTypeAndOperatingFluidFilter: (sheetType: string, filter: string[]) => ApiUrl;
    getEligibleSheetsByProductId: (operatingFluidTypes: string, eligibleForProductId: string) => ApiUrl;
    getSheetsWithSearch: (search: string) => ApiUrl;
    sheetById: (sheetId: string) => ApiUrl;
    sheetFilesById: (sheetId: string) => ApiUrl;
    sheetFileById: (sheetId: string, fileId: string) => ApiUrl;
    deleteFileById: (sheetId: string, fileId: string) => ApiUrl;
    uploadFileBySheetId: (sheetId: string, lang: string) => ApiUrl;
    getSheetsByOperatingFluidTypeIdsAndValidityPeriod: (
        operatingFluidTypeIds: string[],
        validityPeriod: string
    ) => ApiUrl;
    getSheetsByFilterSelect: (queryString: string) => ApiUrl;
    getSheetsByFilterModelFilterAndSearch: (queryString: string, search: string) => ApiUrl;
    updateCompatibleSheets: (sheetId: string) => ApiUrl;
    engineOilTests: ApiUrl;
    getEngineOilTestsForCurrentUserManufacturer: () => ApiUrl;
    getEngineOilTests: () => ApiUrl;
    getEngineOilTestsFieldQuery: (testGroupId: string, queryParam: EngineOilTestFieldQueryParameter) => ApiUrl;
    uploadEngineOilTest: () => ApiUrl;
    updateEngineOilTest: (engineOilTestId: string) => ApiUrl;
    engineOilTestById: (engineOilTestId: string) => ApiUrl;
    deleteEngineOilTestById: (engineOilTestId: string) => ApiUrl;
    engineOilTestFilesById: (engineOilTestId: string) => ApiUrl;
    uploadFileByEngineOilTestId: (engineOilTestId: string, fileType: OilTestingEngineTestsFileTypes) => ApiUrl;
    deleteEngineOilTestFileById: (engineOilTestId: string, fileId: string) => ApiUrl;
    downloadEngineOilTestFileById: (engineOilTestId: string, fileId: string) => ApiUrl;
    manageTests: ApiUrl;
    getAllTestAreas: (operatingFluidTypeId: string, mbSpecificationId: string, sort?: string[]) => ApiUrl;
    getTestAreasSortedBy: (
        operatingFluidTypeId: string,
        mbSpecificationId: string,
        testAreaType: BaseTestAreaDto.testAreaType,
        sort?: string[]
    ) => ApiUrl;
    getTestAreasByOperatingFluidTypeIdSortedBy: (
        operatingFluidTypeId: string,
        testAreaType: BaseTestAreaDto.testAreaType,
        sort?: string[]
    ) => ApiUrl;
    uploadTestArea: () => ApiUrl;
    getTestAreaById: (testAreaId: string) => ApiUrl;
    deleteTestAreaById: (testAreaId: string) => ApiUrl;
    updateTestAreaById: (testAreaId: string) => ApiUrl;
    getTestGroups: (testAreaId: string) => ApiUrl;
    getTestGroupsSortedBy: (testAreaId: string, sort?: string[]) => ApiUrl;
    uploadTestGroup: (testAreaId: string) => ApiUrl;
    getTestGroupById: (testAreaId: string, testGroupId: string) => ApiUrl;
    deleteTestGroupById: (testAreaId: string, testGroupId: string) => ApiUrl;
    updateTestGroupById: (testAreaId: string, testGroupId: string) => ApiUrl;
    getTestStepsBySheetIdAndMbSpecificationIdSortedBy: (
        testGroupId: string,
        mbSpecificationId?: string,
        sheetId?: string
    ) => ApiUrl;
    getTestSteps: (
        testGroupId: string,
        sheetId: string,
        mbSpecificationId: string,
        sort?: string[],
        isActive?: boolean
    ) => ApiUrl;
    uploadTestStep: (testGroupId: string) => ApiUrl;
    getTestStepById: (testGroupId: string, testStepId: string) => ApiUrl;
    deleteTestStepById: (testGroupId: string, testStepId: string) => ApiUrl;
    updateTestStepById: (testGroupId: string, testStepId: string) => ApiUrl;
    getTestParameters: (testStepId: string, sheetId: string, mbSpecificationId: string) => ApiUrl;
    uploadTestParameter: (testStepId: string) => ApiUrl;
    updateTestParameter: (testStepId: string, parameterId: string) => ApiUrl;
    getAllTestStructuresSortedBy: (
        operatingFluidTypeId: string,
        testAreaType: GenericTestAreaDto.testAreaType,
        appendTestStructureTree?: boolean
    ) => ApiUrl;
    getTestAreasByLetterFromMbAvailable: (operatingFluidTypeId: string) => ApiUrl;

    operatingFluidTypes: ApiUrl;
    getOperatingFluidTypes: () => ApiUrl;
    uploadNewOperatingFluidType: () => ApiUrl;
    updateOperatingFluidType: (operatingFluidTypeId: string) => ApiUrl;
    operatingFluidTypeById: (operatingFluidTypeId: string) => ApiUrl;
    saeClasses: ApiUrl;
    getSaeClassesByFilterSelect: (queryString: string) => ApiUrl;
    getSaeClassesByOperatingFluidId: (OperatingFluidId: string) => ApiUrl;
    getSaeClasses: () => ApiUrl;
    uploadNewSaeClass: () => ApiUrl;
    updateSaeClass: (saeClassId: string) => ApiUrl;
    saeClassById: (saeClassId: string) => ApiUrl;
    mbSpecifications: ApiUrl;
    getAllMbSpecifications: () => ApiUrl;
    getMbSpecifications: (operatingFluidTypeId?: string) => ApiUrl;
    uploadNewMbSpecification: (copyFrom?: string) => ApiUrl;
    updateMbSpecification: (mbSpecificationsId: string) => ApiUrl;
    mbSpecificationById: (mbSpecificationsId: string) => ApiUrl;
    qualityLines: ApiUrl;
    getQualityLines: () => ApiUrl;
    uploadNewQualityLine: () => ApiUrl;
    updateQualityLine: (qualityLineId: string) => ApiUrl;
    qualityLineById: (qualityLineId: string) => ApiUrl;
    salesAreas: ApiUrl;
    getSalesAreas: (search?: string) => ApiUrl;
    uploadNewSalesArea: () => ApiUrl;
    updateSalesArea: (salesAreaId: string) => ApiUrl;
    deleteSalesArea: (salesAreaId: string) => ApiUrl;
    salesAreaById: (salesAreaId: string) => ApiUrl;
    sendCotactInformation: () => ApiUrl;
    getInfolettersForCompany: (registrationId: string) => ApiUrl;
    getInfoletterForCompanyById: (registrationId: string, infoletterId: string) => ApiUrl;
    deleteInfoletterForCompanyById: (registrationId: string, infoletterId: string) => ApiUrl;
    downloadInfoletterForCompanyById: (registrationId: string, infoletterId: string) => ApiUrl;
    uploadInfoletterForCompany: (registrationId: string) => ApiUrl;
    getWorkingMaterials: (search?: string) => ApiUrl;
    uploadWorkingMaterialsFile: (fileType: string) => ApiUrl;
    uploadWorkingMaterial: (fileType: string) => ApiUrl;
    updateWorkingMaterial: (workingMaterialId: string) => ApiUrl;
    deleteWorkingMaterial: (workingMaterialId: string) => ApiUrl;
    workingMaterialTemplates: ApiUrl;
    getWorkingMaterialTemplates: () => ApiUrl;
    baseOils: ApiUrl;
    getBaseOils: () => ApiUrl;
    getBaseOilsByOperatingFluidTypeId: (operatingFluidTypeId: string) => ApiUrl;
    getBaseOilsWithSearch: (designation: string) => ApiUrl;
    getBaseOilsByFilterModelFilterAndSearch: (queryString: string, search: string) => ApiUrl;
    getBaseOilsByFilterSelect: (queryString: string) => ApiUrl;
    baseOilById: (baseOilId: string) => ApiUrl;
    uploadNewBaseOil: () => ApiUrl;
    updateBaseOil: (baseOilId: string) => ApiUrl;
    deleteBaseOil: (baseOilId: string) => ApiUrl;
    baseOilFilesById: (baseOilId: string) => ApiUrl;
    baseOilFileById: (baseOilId: string, fileId: string) => ApiUrl;
    uploadFileByBaseOilId: (baseOilId: string) => ApiUrl;
    deleteBaseOilFileById: (baseOilId: string, fileId: string) => ApiUrl;
    additives: ApiUrl;
    additiveById: (additiveId: string) => ApiUrl;
    uploadNewAdditive: () => ApiUrl;
    updateAdditive: (additiveId: string) => ApiUrl;
    deleteAdditive: (additiveId: string) => ApiUrl;
    additiveFilesById: (additiveId: string) => ApiUrl;
    additiveFileById: (additiveId: string, fileId: string) => ApiUrl;
    uploadFileByAdditiveId: (additiveId: string) => ApiUrl;
    deleteAdditiveFileById: (additiveId: string, fileId: string) => ApiUrl;
    getAdditives: () => ApiUrl;
    getAdditivesByOperatingFluidTypeId: (operatingFluidTypeId: string) => ApiUrl;
    getAdditivesWithSearch: (designation: string) => ApiUrl;
    getAdditivesByFilterModelFilterAndSearch: (queryString: string, search: string) => ApiUrl;
    getAdditivesByFilterSelect: (queryString: string) => ApiUrl;
    news: ApiUrl;
    updateNews: (newsId: string) => ApiUrl;
    deleteNews: (newsId: string) => ApiUrl;
    getNews: (pagination: Pageable) => ApiUrl;
    getNewsByNewsState: (pagination: Pageable, newsState: string) => ApiUrl;
    uploadNewNews: () => ApiUrl;
    uploadFileByNewsId: (newsId: string, lang: string) => ApiUrl;
    deleteNewsFileById: (newsId: string, fileId: string) => ApiUrl;
    newsFilesById: (newsId: string) => ApiUrl;
    newsById: (newsId: string) => ApiUrl;
    getNewsById: (newsId: string) => ApiUrl;
    getInfoAboutNewsFileById: (newsId: string) => ApiUrl;
    downloadNewsFileById: (newsId: string, fileId: string) => ApiUrl;
    products: ApiUrl;
    getProductsFilteredByManufacturer: (id: string, options?: ProductApiFilters) => ApiUrl;
    getProducts: (task?: TaskDto.type) => ApiUrl;
    getProductsWithSearch: (search: string) => ApiUrl;
    uploadNewProduct: () => ApiUrl;
    uploadFileByProductId: (productId: string) => ApiUrl;
    getProductFilesById: (productId: string) => ApiUrl;
    deleteProductFileById: (productId: string, fileId: string) => ApiUrl;
    getProductById: (productId: string) => ApiUrl;
    getProductChangeabilityStatus: (productId: string) => ApiUrl;
    deleteProductById: (productId: string) => ApiUrl;
    updateProductById: (productId: string) => ApiUrl;
    getProductSheets: (productId: string) => ApiUrl;
    uploadProductSheet: (productId: string) => ApiUrl;
    getProductSheetById: (productId: string, productSheetId: string) => ApiUrl;
    deleteProductSheetById: (productId: string, productSheetId: string) => ApiUrl;
    oilTestingEngineMasterData: ApiUrl;
    getEngineTypes: () => ApiUrl;
    getEngineTypeById: (engineTypeId: string) => ApiUrl;
    uploadNewEngineType: () => ApiUrl;
    deleteEngineTypeById: (engineTypeId: string) => ApiUrl;
    updateEngineTypeById: (engineTypeId: string) => ApiUrl;
    getFuels: () => ApiUrl;
    getFuelById: (fuelId: string) => ApiUrl;
    uploadNewFuel: () => ApiUrl;
    deleteFuelById: (fuelId: string) => ApiUrl;
    updateFuelById: (fuelId: string) => ApiUrl;
    getInvoiceAmount: () => ApiUrl;
    getInvoiceAmountById: (invoiceId: string) => ApiUrl;
    uploadNewInvoiceAmount: () => ApiUrl;
    deleteInvoiceAmountById: (invoiceId: string) => ApiUrl;
    updateInvoiceAmountById: (invoiceId: string) => ApiUrl;
    getTestStands: () => ApiUrl;
    getTestStandById: (testStandId: string) => ApiUrl;
    uploadNewTestStand: () => ApiUrl;
    deleteTestStandById: (testStandId: string) => ApiUrl;
    updateTestStandById: (testStandId: string) => ApiUrl;
    getTestBenchResults: () => ApiUrl;
    getTestBenchResultById: (testResultId: string) => ApiUrl;
    uploadNewTestBenchResult: () => ApiUrl;
    deleteTestBenchResultById: (testResultId: string) => ApiUrl;
    updateTestBenchResultById: (testResultId: string) => ApiUrl;
    serviceAreas: ApiUrl;
    getServiceAreas: () => ApiUrl;
    getServiceAreasSortedBy: (sort: string[]) => ApiUrl;
    uploadNewServiceArea: () => ApiUrl;
    uploadFileByServiceAreaId: (serviceAreaId: string, lang: string) => ApiUrl;
    getServiceAreaFilesById: (serviceAreaId: string) => ApiUrl;
    deleteServiceAreaFileById: (serviceAreaId: string, fileId: string) => ApiUrl;
    downloadServiceAreaFileById: (serviceAreaId: string, fileId: string) => ApiUrl;
    getServiceAreaById: (serviceAreaId: string) => ApiUrl;
    deleteServiceAreaById: (serviceAreaId: string) => ApiUrl;
    updateServiceAreaById: (serviceAreaId: string) => ApiUrl;
    getFAQs: () => ApiUrl;
    updateFAQById: (FAQId: string) => ApiUrl;
    deleteFAQById: (FAQId: string) => ApiUrl;
    uploadNewFAQ: () => ApiUrl;
    uploadFileByFAQId: (FAQId: string, lang: string) => ApiUrl;
    getFAQFilesById: (FAQId: string) => ApiUrl;
    deleteFAQFileById: (FAQId: string, fileId: string) => ApiUrl;
    downloadFAQFileById: (FAQId: string, fileId: string) => ApiUrl;
    getFAQById: (FAQId: string) => ApiUrl;
    getDblSpecifications: () => ApiUrl;
    dblCategories: ApiUrl;
    getDblCategories: () => ApiUrl;
    getDblCategory: (dblCategoryId: string) => ApiUrl;
    uploadDblCategory: () => ApiUrl;
    deleteDblCategoryById: (dblCategoryId: string) => ApiUrl;
    updateDblCategoryById: (dblCategoryId: string) => ApiUrl;
    dblSpecifications: ApiUrl;
    getDblSpecificationsByCatagoryId: (dblCategoryId: string) => ApiUrl;
    getDblSpecification: (dblCategoryId: string, dblSpecificationId: string) => ApiUrl;
    uploadDblSpecifications: (dblCategoryId: string) => ApiUrl;
    deleteDblSpecificationById: (dblCategoryId: string, dblSpecificationId: string) => ApiUrl;
    updateDblSpecificationById: (dblCategoryId: string, dblSpecificationId: string) => ApiUrl;
    thirdParties: ApiUrl;
    getThirdParties: (companyId: string) => ApiUrl;
    uploadThirdParty: () => ApiUrl;
    updateThirdParty: (thirdPartyId: string) => ApiUrl;
    getAllThirdParties: () => ApiUrl;
    getThirdPartyById: (companyId: string) => ApiUrl;
    uploadFileByThirdPartyId: (thirdPartyId: string, fileType: ThirdPartyContractFileType) => ApiUrl;
    getFilesByThirdPartyId: (thirdPartyId: string) => ApiUrl;
    deleteThirdPartyFileById: (thirdPartyId: string, filesId: string) => ApiUrl;
    downloadThirdPartyFileById: (thirdPartyId: string, fileId: string) => ApiUrl;
    containerSize: ApiUrl;
    getContainerSizes: (search?: string) => ApiUrl;
    uploadNewContainerSize: () => ApiUrl;
    updateContainerSize: (containerSizeId: string) => ApiUrl;
    deleteContainerSize: (containerSizeId: string) => ApiUrl;
    containerSizeById: (containerSizeId: string) => ApiUrl;
    vehicles: ApiUrl;
    getVehicles: () => ApiUrl;
    uploadVehiclesFile: () => ApiUrl;
    invoices: ApiUrl;
    uploadFileByInvoiceId: (invoiceId: string, fileType: InvoiceFileType) => ApiUrl;
    getInvoiceFileByIdContent: (invoiceId: string, fileId: string) => ApiUrl;
    getInvoices: () => ApiUrl;
    getInvoicesByInvoiceState: (invoiceStates?: string[]) => ApiUrl;
    getInvoicesWithSearch: (designation: string) => ApiUrl;
    getInvoicesByFilterModelFilterAndSearch: (queryString: string, search: string) => ApiUrl;
    getInvoicesByFilterSelect: (queryString: string) => ApiUrl;
    getInvoiceFileById: (invoiceId: string) => ApiUrl;
    deleteInvoiceFileById: (invoiceId: string, fileId: string) => ApiUrl;
    getInvoicesByCompanyId: (companyId: string) => ApiUrl;
    updateNewInvoice: (invoiceId: string) => ApiUrl;
    getInvoiceById: (invoiceId: string) => ApiUrl;
    formulations: ApiUrl;
    getProductFormulationById: (productId: string, formulationId: string) => ApiUrl;
    updateProductFormulationById: (productId: string, formulationId: string, setDraftMode?: boolean) => ApiUrl;
    deleteProductFormulationById: (productId: string, formulationId: string) => ApiUrl;
    getProductFormulations: (productId: string, options?: ProductFormulationApiFilters) => ApiUrl;
    getProductFormulationsOnlyFulfilled: (productId: string, fullFIlled: boolean, includeRenewalApprovalRequired?: boolean) => ApiUrl;
    uploadProductFormulation: (productId: string) => ApiUrl;
    getProductFormulationFiles: (productId: string, formulationId: string) => ApiUrl;
    uploadProductFormulationFile: (productId: string, formulationId: string, fileType: FormulationFileTypes) => ApiUrl;
    getProductFormulationFile: (productId: string, formulationId: string, fileId: string) => ApiUrl;
    deleteProductFormulationFile: (productId: string, formulationId: string, fileId: string) => ApiUrl;
    getProductFormulationTestStepValues: (
        productId: string,
        formulationId: string,
        testGroupId: string,
        letterNumber?: string
    ) => ApiUrl;
    getAdditiveBaseOilTestStepValues: (
        productId: string,
        formulationId: string,
        valueType:
            | TestStepValueWrapper.testStepValueWrapperType.ADDITIVE
            | TestStepValueWrapper.testStepValueWrapperType.BASE_OIL
    ) => ApiUrl;
    activeProductFormulation: (productId: string, productFormulationId: string) => ApiUrl;

    updateProductFormulationTestStepValues: (productId: string, formulationId: string) => ApiUrl;
    getSheetsForManufacturerProduct: () => ApiUrl;
    getProductFormulationTestProgress: (productId: string, formulationId: string) => ApiUrl;
    getCostForProductApproval: (productType: string, processType: string, tradeAuth: boolean) => ApiUrl;
    uploadNewRelease: (productId: string) => ApiUrl;
    getProductApprovalsByProductId: (productId: string, size?: number) => ApiUrl;
    getProductApprovalByProductIdAndId: (productId: string, productApprovalId: string) => ApiUrl;
    getProductApprovals: () => ApiUrl;
    getProductApprovalsWithState: (state: string) => ApiUrl;
    getProductApprovalsWithSearch: (search: string) => ApiUrl;
    getProductApprovalsWithStateAndSearch: (state: string, search: string) => ApiUrl;
    getProductApprovalById: (productApprovalId: string) => ApiUrl;
    getProductApprovalCheckStepsUser: (productId: string, productApprovalId: string) => ApiUrl;
    getProductApprovalCheckStepsAdmin: (productApprovalId: string) => ApiUrl;
    updateProductApprovalCheckStep: (productApprovalId: string, checkStepId: string) => ApiUrl;
    getProductApprovalCheckStepFilesUser: (productId: string, productApprovalId: string, checkStepId: string) => ApiUrl;
    getProductApprovalCheckStepFileContent: (
        productId: string,
        productApprovalId: string,
        checkStepId: string,
        fileMetaDataId: string
    ) => ApiUrl;
    updateProductApprovalCheckStepFile: (
        productApprovalId: string,
        checkStepId: string,
        checkStepFileType: ProcessDetailCheckStepFileType
    ) => ApiUrl;
    getProductApprovalCheckStepFiles: (productApprovalId: string, checkStepId: string) => ApiUrl;
    getProductApprovalCheckStepFileById: (productApprovalId: string, checkStepId: string, fileId: string) => ApiUrl;
    uploadSignedReleaseEngagementFile: (productId: string, productApprovalId: string, checkStepId: string) => ApiUrl;
    updateSignedReleaseEngagementFile: (
        productId: string,
        productApprovalId: string,
        checkStepId: string,
        fileMetaDataId: string
    ) => ApiUrl;
    updateProductApprovalState: (productApprovalId: string, skipValidations?: boolean) => ApiUrl;
    cancelProductApproval: (productId: string, productApprovalId: string) => ApiUrl;
    cancelProductApprovalWithDependents: (productId: string, productApprovalId: string) => ApiUrl;
    rejectProductApprovalWithDependents: (productApprovalId: string) => ApiUrl;
    resubmitProductApproval: (productId: string, productApprovalId: string) => ApiUrl;
    getPublicFile: (publicFile: PublicFiles, lang: string) => ApiUrl;
    getLegacyCompanies: () => ApiUrl;
    uploadProductFormulationAnnualReport: (productId: string, formulationId: string) => ApiUrl;
    getProductFormulationAnnualReport: (productId: string, formulationId: string) => ApiUrl;
    getTasks: () => ApiUrl;
    getMasterDataSubstanceTypes: (search?: string) => ApiUrl;
    getMasterDataSubstanceTypeById: (substanceTypeId: string) => ApiUrl;
    deleteMasterDataSubstanceTypeById: (substanceTypeId: string) => ApiUrl;
    createMasterDataSubstanceType: () => ApiUrl;
    updateMasterDataSubstanceType: (substanceTypeId: string) => ApiUrl;
    getMasterDataSubstanceTypesByCategory: (category: string) => ApiUrl;
}

function createGetProductFormulations(servicePath: string) {
    return (productId: string, options: ProductFormulationApiFilters = {}) => {
        const params = new URLSearchParams();

        if (options.onlyActive) {
            params.append(onlyActiveParam, 'true');
        }

        if (options.onlyTechnicallyApproved) {
            params.append(onlyTechnicallyApprovedParam, 'true');
        }

        const queryString = params.toString();

        return queryString
            ? `${servicePath}/${products}/${productId}/${formulations}?${queryString}`
            : `${servicePath}/${products}/${productId}/${formulations}`;
    };
}

/**
 * URL composer for task service API urls
 *
 * @param {string} baseUrl
 * @return {*}
 */
function getUserApiUrls(baseUrl: string, serviceVersion?: string) {
    const serviceName = 'api';
    const servicePath = `${baseUrl}/${serviceName}` + (serviceVersion ? `/${serviceVersion}` : '');

    return {
        registrations: () => `${servicePath}/${registrationsPrefix}`,
        registrationById: (registrationId: string) => `${servicePath}/${registrationsPrefix}/${registrationId}`,
        registrationFileById: (registrationId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${companyPresentationPrefix}`,
        deleteFileByIdForRegistrationById: (registrationId: string, presentationId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${companyPresentationPrefix}/${presentationId}`,
        downloadFileByIdForRegistrationById: (registrationId: string, presentationId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${companyPresentationPrefix}/${presentationId}/content`,
        userInfo: `${servicePath}/${userInfoPrefix}`,
        updateSalutation: () => `${servicePath}/${userInfoPrefix}`,
        sendCotactInformation: () => `${servicePath}/${contactPrefix}`,
        uploadInfoletterForCompany: (registrationId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${infolettersPrefix}`,
        deleteInfoletterForCompanyById: (registrationId: string, infoletterId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${infolettersPrefix}/${infoletterId}`,
        getInfolettersForCompany: (registrationId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${infolettersPrefix}`,
        news: `${servicePath}/${news}`,
        getNews: (pagination?: Pageable) =>
            !pagination
                ? `${servicePath}/${news}`
                : `${servicePath}/${news}?page=${pagination.page}&size=${pagination.size
                }&sort=${pagination.sort?.join('&sort=')}`,
        getNewsById: (newsId: string) => `${servicePath}/${news}/${newsId}`,
        getInfoAboutNewsFileById: (newsId: string) => `${servicePath}/${news}/${newsId}/files`,
        downloadNewsFileById: (newsId: string, fileId: string) =>
            `${servicePath}/${news}/${newsId}/files/${fileId}/content`,
        products: `${servicePath}/${products}`,
        getProducts: (task?: TaskDto.type) => `${servicePath}/${products}${task ? `?task=${task}` : ''}`,
        uploadNewProduct: () => `${servicePath}/${products}`,
        uploadFileByProductId: (productId: string) => `${servicePath}/${products}/${productId}/files`,
        getProductFilesById: (productId: string) => `${servicePath}/${products}/${productId}/files`,
        getProductById: (productId: string) => `${servicePath}/${products}/${productId}`,
        getProductChangeabilityStatus: (productId: string) =>
            `${servicePath}/${products}/${productId}/changeability-status`,
        deleteProductById: (productId: string) => `${servicePath}/${products}/${productId}`,
        updateProductById: (productId: string) => `${servicePath}/${products}/${productId}`,
        deleteProductFileById: (productId: string, fileId: string) =>
            `${servicePath}/${products}/${productId}/files/${fileId}`,
        getProductSheets: (productId: string) => `${servicePath}/${products}/${productId}/sheets`,
        uploadProductSheet: (productId: string) => `${servicePath}/${products}/${productId}/sheets`,
        getProductSheetById: (productId: string, productSheetId: string) =>
            `${servicePath}/${products}/${productId}/sheets/${productSheetId}`,
        deleteProductSheetById: (productId: string, productSheetId: string) =>
            `${servicePath}/${products}/${productId}/sheets/${productSheetId}`,
        operatingFluidTypes: `${servicePath}/${operatingFluidTypes}`,
        getOperatingFluidTypes: () => `${servicePath}/${operatingFluidTypes}`,
        sheets: `${servicePath}/${sheets}`,
        getSheetsWithSheetType: (sheetType: string) => `${servicePath}/${sheets}?sheetType=${sheetType}`,
        getSheetsWithSheetTypeAndOperatingFluidFilter: (sheetType: string, filter: string[]) =>
            `${servicePath}/${sheets}?sheetType=${sheetType}&operatingFluidTypes=${filter}`,
        saeClasses: `${servicePath}/${saeClasses}`,
        getEligibleSheetsByProductId: (operatingFluidTypes: string, eligibleForProductId: string) =>
            `${servicePath}/${sheets}?operatingFluidTypes=${operatingFluidTypes}&eligibleForProductId=${eligibleForProductId}`,
        getSaeClassesByFilterSelect: (queryString: string) => `${servicePath}/${saeClasses}?${queryString}`,
        getSaeClasses: () => `${servicePath}/${saeClasses}`,
        getSaeClassesByOperatingFluidId: (OperatingFluidId: string) =>
            `${servicePath}/${saeClasses}?operatingFluidTypes=${OperatingFluidId}`,
        mbSpecifications: `${servicePath}/${mbSpecifications}`,
        getMbSpecifications: (operatingFluidTypeId?: string) => {
            if (!operatingFluidTypeId) {
                return `${servicePath}/${mbSpecifications}`;
            }
            return `${servicePath}/${mbSpecifications}?operatingFluidTypeId=${operatingFluidTypeId}`;
        },
        salesAreas: `${servicePath}/${salesAreas}`,
        getSalesAreas: () => `${servicePath}/${salesAreas}`,
        serviceAreas: `${servicePath}/${serviceAreas}`,
        getServiceAreas: () => `${servicePath}/${serviceAreas}`,
        getServiceAreasSortedBy: (sort: string[]) =>
            `${servicePath}/${serviceAreas}?&sort=${sort?.join(',')}&${maxPaginationSettingString}`,
        getFAQs: () => `${servicePath}/${frequentlyAskedQuestions}`,
        downloadFAQFileById: (FAQIdId: string, fileId: string) =>
            `${servicePath}/${frequentlyAskedQuestions}/${FAQIdId}/files/${fileId}/content`,
        getServiceAreaFilesById: (serviceAreaId: string) => `${servicePath}/${serviceAreas}/${serviceAreaId}/files`,
        downloadServiceAreaFileById: (serviceAreaId: string, fileId: string) =>
            `${servicePath}/${serviceAreas}/${serviceAreaId}/files/${fileId}/content`,
        updateThirdParty: (thirdPartyId: string) => `${servicePath}/${thirdParties}/${thirdPartyId}`,
        getAllThirdParties: () => `${servicePath}/${thirdParties}`,
        getThirdPartyById: (companyId: string) => `${servicePath}/${thirdParties}?companyId=${companyId}`,
        getDblSpecifications: () => `${servicePath}/${dblSpecifications}`,
        getDblCategories: () => `${servicePath}/${dblCategories}?${maxPaginationSettingString}`,
        getContainerSizes: (search?: string) =>
            search ? `${servicePath}/${containerSize}?search=${search}` : `${servicePath}/${containerSize}`,
        formulations: `${servicePath}/${formulations}`,
        getProductFormulationById: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}`,
        updateProductFormulationById: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}`,
        deleteProductFormulationById: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}`,
        getProductFormulations: createGetProductFormulations(servicePath),
        getProductFormulationsOnlyFulfilled: (productId: string, fullFIlled: boolean, includeRenewalApprovalRequired?: boolean) => {
            let url = `${servicePath}/${products}/${productId}/${formulations}?${onlyFullFIlled}=${fullFIlled}`;
            if (includeRenewalApprovalRequired) {
                url += '&includeRenewalApprovalRequired=true';
            }
            url += `&${maxPaginationSettingString}`;
            return url;
        },
        uploadProductFormulation: (productId: string) => `${servicePath}/${products}/${productId}/${formulations}`,
        getProductFormulationFiles: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/files`,
        uploadProductFormulationFile: (productId: string, formulationId: string, fileType: FormulationFileTypes) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/files?fileType=${fileType}`,
        getProductFormulationFile: (productId: string, formulationId: string, fileId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/files/${fileId}/content`,
        deleteProductFormulationFile: (productId: string, formulationId: string, fileId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/files/${fileId}`,
        getAllTestAreas: (operatingFluidTypeId: string, mbSpecificationId: string, sort?: string[]) =>
            `${servicePath}/${testAreas}?${operatingFluidTypeIdParam}=${operatingFluidTypeId}&${mbSpecificationIdParam}=${mbSpecificationId}${sort ? `&sort=${sort?.join(',')}` : ''}`,
        getTestAreasSortedBy: (
            operatingFluidTypeId: string,
            mbSpecificationId: string,
            testAreaType: BaseTestAreaDto.testAreaType,
            sort?: string[]
        ) => {
            const sortParam = sort && sort.length ? `&sort=${sort.join(',')}` : '';
            return `${servicePath}/${testAreas}?${operatingFluidTypeIdParam}=${operatingFluidTypeId}&testAreaType=${testAreaType}&${mbSpecificationIdParam}=${mbSpecificationId}&${maxPaginationSettingString}${sortParam}`;
        },
        getTestAreasByOperatingFluidTypeIdSortedBy: (
            operatingFluidTypeId: string,
            testAreaType: BaseTestAreaDto.testAreaType,
            sort?: string[]
        ) => {
            const sortParam = sort?.length ? `&sort=${sort.join(',')}` : '';
            return `${servicePath}/${testAreas}?${operatingFluidTypeIdParam}=${operatingFluidTypeId}&testAreaType=${testAreaType}&${maxPaginationSettingString}${sortParam}`;
        },
        getTestGroupsSortedBy: (testAreaId: string, sort?: string[]) => {
            const sortParam = sort?.length ? `&sort=${sort.join(',')}` : '';
            return `${servicePath}/${testAreas}/${testAreaId}/${testGroups}?${maxPaginationSettingString}${sortParam}`;
        },
        getTestGroups: (testAreaId: string) => `${servicePath}/${testAreas}/${testAreaId}/${testGroups}`,
        getTestSteps: (testGroupId: string, sheetId: string, mbSpecificationId: string, sort: string[]) => {
            let url = `${servicePath}/${testGroups}/${testGroupId}/${testSteps}?${sheetIdParam}=${sheetId}&${mbSpecificationIdParam}=${mbSpecificationId}&${maxPaginationSettingString}`;

            if (sort?.length) {
                url += `&sort=${sort.join(',')}`;
            }

            return url;
        },
        getEngineOilTestsFieldQuery: (testGroupId: string, queryParam: EngineOilTestFieldQueryParameter) =>
            `${servicePath}/${engineOilTests}/${fields}?field=${queryParam}&${testGroupIdParam}=${testGroupId}`,
        getEngineOilTestsForCurrentUserManufacturer: () => `${servicePath}/${engineOilTests}`,
        engineOilTestFilesById: (engineOilTestId: string) =>
            `${servicePath}/${engineOilTests}/${engineOilTestId}/files`,
        uploadFileByEngineOilTestId: (engineOilTestId: string, fileType: OilTestingEngineTestsFileTypes) =>
            `${servicePath}/${engineOilTests}/${engineOilTestId}/files?fileType=${fileType}`,
        downloadEngineOilTestFileById: (engineOilTestId: string, fileId: string) =>
            `${servicePath}/${engineOilTests}/${engineOilTestId}/files/${fileId}/content`,
        getProductFormulationTestStepValues: (
            productId: string,
            formulationId: string,
            testGroupId: string,
            letterNumber?: string
        ) => {
            const letterNumberParamString = letterNumber ? `&${letterNumberParam}=${letterNumber}` : '';
            return `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/${testValues}?${testGroupIdParam}=${testGroupId}&${maxPaginationSettingString}${letterNumberParamString}`;
        },
        getAdditiveBaseOilTestStepValues: (
            productId: string,
            formulationId: string,
            valueType:
                | TestStepValueWrapper.testStepValueWrapperType.ADDITIVE
                | TestStepValueWrapper.testStepValueWrapperType.BASE_OIL
        ) => {
            return `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/${testValues}?valueType=${valueType}`;
        },
        updateProductFormulationTestStepValues: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/${testValues}`,
        getProductFormulationTestProgress: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/${testProgress}`,
        getSheets: () => `${servicePath}/${sheets}`,
        getCostForProductApproval: (productType: string, processType: string, tradeAuth: boolean) =>
            `${servicePath}/${costs}/?${processTypeIdentifier}=${processType}&${productTypeIdentifier}=${productType}&${tradeAuthorization}=${tradeAuth}`,
        uploadNewRelease: (productId: string) => `${servicePath}/${products}/${productId}/${approvals}`,
        getProductApprovalByProductIdAndId: (productId: string, productApprovalId: string) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}`,
        getProductApprovalsByProductId: (productId: string) => `${servicePath}/${products}/${productId}/${approvals}`,
        getProductApprovalById: (productApprovalId: string) =>
            `${servicePath}/${productApprovals}/${productApprovalId}`,
        getProductApprovalCheckStepsUser: (productId: string, productApprovalId: string) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}/${checkSteps}`,
        getProductApprovalCheckStepFilesUser: (productId: string, productApprovalId: string, checkStepId: string) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}/${checkSteps}/${checkStepId}/files`,
        getProductApprovalCheckStepFileContent: (
            productId: string,
            productApprovalId: string,
            checkStepId: string,
            fileMetaDataId: string
        ) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}/${checkSteps}/${checkStepId}/files/${fileMetaDataId}/content`,
        uploadSignedReleaseEngagementFile: (productId: string, productApprovalId: string, checkStepId: string) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}/${checkSteps}/${checkStepId}/files?checkStepFileType=${ProductApprovalCheckStepFileType.RELEASE_ENGAGEMENT_SIGNED}`,
        updateSignedReleaseEngagementFile: (
            productId: string,
            productApprovalId: string,
            checkStepId: string,
            fileMetaDataId: string
        ) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}/${checkSteps}/${checkStepId}/files/${fileMetaDataId}?checkStepFileType=${ProductApprovalCheckStepFileType.RELEASE_ENGAGEMENT_SIGNED}`,
        cancelProductApproval: (productId: string, productApprovalId: string) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}/cancel`,
        cancelProductApprovalWithDependents: (productId: string, productApprovalId: string) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}/cancel-with-dependents`,
        resubmitProductApproval: (productId: string, productApprovalId: string) =>
            `${servicePath}/${products}/${productId}/${approvals}/${productApprovalId}/resubmit`,
        getSheetsForManufacturerProduct: () => `${servicePath}/${sheets}`,
        uploadFileByInvoiceId: (invoiceId: string, fileType: InvoiceFileType) =>
            `${servicePath}/${invoices}/${invoiceId}/files?fileType=${fileType}`,
        getInvoiceFileByIdContent: (invoiceId: string, fileId: string) =>
            `${servicePath}/${invoices}/${invoiceId}/files/${fileId}/content`,
        getInvoices: () => `${servicePath}/${invoices}`,
        getInvoicesByInvoiceState: (invoiceStates?: string[]) => {
            if (invoiceStates) {
                return `${servicePath}/${invoices}?${invoiceStatesParam}=${invoiceStates.join('&')}`;
            }
            return `${servicePath}/${invoices}`;
        },
        getInvoiceById: (invoiceId: string) => `${servicePath}/${invoices}/${invoiceId}`,
        getInvoiceFileById: (invoiceId: string) => `${servicePath}/${invoices}/${invoiceId}/files`,
        getInfoletterForCompanyById: (registrationId: string, infoletterId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${infolettersPrefix}/${infoletterId}`,
        getAdditivesByOperatingFluidTypeId: (operatingFluidTypeId: string) =>
            `${servicePath}/${additives}?operatingFluidTypeId=${operatingFluidTypeId}`,
        getBaseOilsByOperatingFluidTypeId: (operatingFluidTypeId: string) =>
            `${servicePath}/${baseOils}?operatingFluidTypeId=${operatingFluidTypeId}`,
        uploadProductFormulationAnnualReport: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/annual-reports`,
        getProductFormulationAnnualReport: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/annual-reports`,
        getTasks: () => `${servicePath}/tasks`,
        getMasterDataSubstanceTypesByCategory: (category: string) => {
            return `${servicePath}/${substanceTypesParam}?${categoryParam}=${category}`;
        },
    } as ApiUrlTask;
}

function getAdminApiUrls(baseUrl: string, serviceVersion?: string) {
    const serviceName = 'admin-api';
    const servicePath = `${baseUrl}/${serviceName}` + (serviceVersion ? `/${serviceVersion}` : '');

    return {
        adminInfo: `${servicePath}/admin-info`,
        registrations: (states?: CompanyRegistrationDto.registrationState[], search?: string) => {
            const base = `${servicePath}/${registrationsPrefix}`;
            const params: string[] = [];
            if (search) {
                params.push(`search=${encodeURIComponent(search)}`);
            }
            if (states && states.length > 0) {
                params.push(...states.map((state) => `registration_state=${encodeURIComponent(state)}`));
            }
            return params.length > 0 ? `${base}?${params.join('&')}` : base;
        },
        pagableToChangeRegistrations: (pagination?: Pageable) =>
            !pagination
                ? `${servicePath}/${registrationsPrefix}?${latestHistoryChangesAccepted}=false`
                : `${servicePath}/${registrationsPrefix}?page=${pagination.page}&${latestHistoryChangesAccepted}=false`,
        pagableToChangeRegistrationsFilterByInfoLetterState: (infoLetterState: string) =>
            `${servicePath}/${registrationsPrefix}?${latestHistoryChangesAccepted}=false&info_letter_state=${infoLetterState}`,
        checksById: (id: string) => `${servicePath}/${checksPrefix}/${id}/files`,
        checkFilesById: (stepId: string, id: string) => `${servicePath}/${checksPrefix}/${stepId}/files/${id}`,
        getCheckStepFileById: (stepId: string, id: string) =>
            `${servicePath}/${checksPrefix}/${stepId}/files/${id}/content`,
        registrationById: (registrationId: string) => `${servicePath}/${registrationsPrefix}/${registrationId}`,
        registrationsCheckById: (registrationId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/checks`,
        registrationFileById: (registrationId: string) =>
            `${servicePath}/company-registrations/${registrationId}/company-presentations`,
        registrationsCheckByIdStepById: (registrationId: string, stepId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/checks/${stepId}`,
        costs: `${servicePath}/${costs}`,
        getCosts: () => `${servicePath}/${costs}`,
        downloadFileByIdForRegistrationById: (registrationId: string, presentationId: string) =>
            `${servicePath}/company-registrations/${registrationId}/company-presentations/${presentationId}`,
        uploadNewCost: () => `${servicePath}/${costs}`,
        costById: (costId: string) => `${servicePath}/${costs}/${costId}`,
        deleteCost: (costId: string) => `${servicePath}/${costs}/${costId}`,
        sheets: `${servicePath}/${sheets}`,
        getSheets: () => `${servicePath}/${sheets}`,
        getSheetsWithSearch: (search: string) => `${servicePath}/${sheets}?search=${search}`,
        sheetById: (sheetId: string) => `${servicePath}/${sheets}/${sheetId}`,
        sheetFilesById: (sheetId: string) => `${servicePath}/${sheets}/${sheetId}/files`,
        sheetFileById: (sheetId: string, fileId: string) =>
            `${servicePath}/${sheets}/${sheetId}/files/${fileId}/content`,
        deleteFileById: (sheetId: string, fileId: string) => `${servicePath}/${sheets}/${sheetId}/files/${fileId}`,
        uploadFileBySheetId: (sheetId: string, lang: string) =>
            `${servicePath}/${sheets}/${sheetId}/files?fileLanguage=${lang}`,
        getSheetsByFilterSelect: (queryString: string) => `${servicePath}/${sheets}?${queryString}`,
        getSheetsByOperatingFluidTypeIdsAndValidityPeriod: (operatingFluidTypeIds: string[], validityPeriod: string) =>
            `${servicePath}/${sheets}?validityPeriod=${validityPeriod}&operatingFluidTypes=${operatingFluidTypeIds.join('&operatingFluidTypes=')}`,
        updateCompatibleSheets: (sheetId: string) => `${servicePath}/${sheets}/${sheetId}/compatible-sheets`,
        engineOilTests: `${servicePath}/${engineOilTests}`,
        getEngineOilTests: () => `${servicePath}/${engineOilTests}`,
        uploadEngineOilTest: () => `${servicePath}/${engineOilTests}`,
        updateEngineOilTest: (engineOilTestId: string) => `${servicePath}/${engineOilTests}/${engineOilTestId}`,
        engineOilTestById: (engineOilTestId: string) => `${servicePath}/${engineOilTests}/${engineOilTestId}`,
        deleteEngineOilTestById: (engineOilTestId: string) => `${servicePath}/${engineOilTests}/${engineOilTestId}`,
        engineOilTestFilesById: (engineOilTestId: string) =>
            `${servicePath}/${engineOilTests}/${engineOilTestId}/files`,
        uploadFileByEngineOilTestId: (engineOilTestId: string, fileType: OilTestingEngineTestsFileTypes) =>
            `${servicePath}/${engineOilTests}/${engineOilTestId}/files?fileType=${fileType}`,
        deleteEngineOilTestFileById: (engineOilTestId: string, fileId: string) =>
            `${servicePath}/${engineOilTests}/${engineOilTestId}/files/${fileId}`,
        downloadEngineOilTestFileById: (engineOilTestId: string, fileId: string) =>
            `${servicePath}/${engineOilTests}/${engineOilTestId}/files/${fileId}/content`,
        manageTests: `${servicePath}/${testAreas}`,
        getTestAreasSortedBy: (
            operatingFluidTypeId: string,
            mbSpecificationId: string,
            testAreaType: BaseTestAreaDto.testAreaType,
            sort?: string[]
        ) => {
            const sortParam = sort?.length ? `&sort=${sort.join(',')}` : '';
            return `${servicePath}/${testAreas}?${operatingFluidTypeIdParam}=${operatingFluidTypeId}&testAreaType=${testAreaType}&${mbSpecificationIdParam}=${mbSpecificationId}&${maxPaginationSettingString}${sortParam}`;
        },
        getTestAreasByOperatingFluidTypeIdSortedBy: (
            operatingFluidTypeId: string,
            testAreaType: BaseTestAreaDto.testAreaType,
            sort?: string[]
        ) => {
            const sortParam = sort?.length ? `&sort=${sort.join(',')}` : '';
            return `${servicePath}/${testAreas}?${operatingFluidTypeIdParam}=${operatingFluidTypeId}&testAreaType=${testAreaType}&${maxPaginationSettingString}${sortParam}`;
        },
        uploadTestArea: () => `${servicePath}/${testAreas}`,
        getTestAreaById: (testAreaId: string) => `${servicePath}/${testAreas}/${testAreaId}`,
        deleteTestAreaById: (testAreaId: string) => `${servicePath}/${testAreas}/${testAreaId}`,
        updateTestAreaById: (testAreaId: string) => `${servicePath}/${testAreas}/${testAreaId}`,
        getTestGroupsSortedBy: (testAreaId: string, sort: string[]) => {
            const sortParam = sort?.length ? `&sort=${sort.join(',')}` : '';
            return `${servicePath}/${testAreas}/${testAreaId}/${testGroups}?${maxPaginationSettingString}${sortParam}`;
        },
        uploadTestGroup: (testAreaId: string) => `${servicePath}/${testAreas}/${testAreaId}/${testGroups}`,
        getTestGroupById: (testAreaId: string, testGroupId: string) =>
            `${servicePath}/${testAreas}/${testAreaId}/${testGroups}/${testGroupId}`,
        deleteTestGroupById: (testAreaId: string, testGroupId: string) =>
            `${servicePath}/${testAreas}/${testAreaId}/${testGroups}/${testGroupId}`,
        updateTestGroupById: (testAreaId: string, testGroupId: string) =>
            `${servicePath}/${testAreas}/${testAreaId}/${testGroups}/${testGroupId}`,
        getTestStepsBySheetIdAndMbSpecificationIdSortedBy: (
            testGroupId: string,
            mbSpecificationId?: string,
            sheetId?: string
        ) => {
            const baseUrl = `${servicePath}/${testGroups}/${testGroupId}/${testSteps}`;

            const params = new URLSearchParams();

            if (sheetId) {
                params.append('sheetId', sheetId);
            }

            if (mbSpecificationId) {
                params.append('mbSpecificationId', mbSpecificationId);
            }

            const queryString = params.toString();
            return queryString ? `${baseUrl}?${queryString}` : baseUrl;
        },
        getTestSteps: (
            testGroupId: string,
            sheetId: string,
            mbSpecificationId: string,
            sort: string[],
            isActive: boolean
        ) => {
            let url = `${servicePath}/${testGroups}/${testGroupId}/${testSteps}?${sheetIdParam}=${sheetId}&${mbSpecificationIdParam}=${mbSpecificationId}&${maxPaginationSettingString}`;

            if (sort?.length) {
                url += `&sort=${sort.join(',')}`;
            }

            if (isActive === true) {
                url += `&isActive=true`;
            }

            return url;
        },
        uploadTestStep: (testGroupId: string) => `${servicePath}/${testGroups}/${testGroupId}/${testSteps}`,
        getTestStepById: (testGroupId: string, testStepId: string) =>
            `${servicePath}/${testGroups}/${testGroupId}/${testSteps}/${testStepId}`,
        deleteTestStepById: (testGroupId: string, testStepId: string) =>
            `${servicePath}/${testGroups}/${testGroupId}/${testSteps}/${testStepId}`,
        updateTestStepById: (testGroupId: string, testStepId: string) =>
            `${servicePath}/${testGroups}/${testGroupId}/${testSteps}/${testStepId}`,
        getTestParameters: (testStepId: string, sheetId: string, mbSpecificationId: string) =>
            `${servicePath}/${testSteps}/${testStepId}/${parameters}?${sheetIdParam}=${sheetId}&${mbSpecificationIdParam}=${mbSpecificationId}`,
        uploadTestParameter: (testStepId: string) => `${servicePath}/${testSteps}/${testStepId}/${parameters}`,
        updateTestParameter: (testStepId: string, parameterId: string) =>
            `${servicePath}/${testSteps}/${testStepId}/${parameters}/${parameterId}`,
        operatingFluidTypes: `${servicePath}/${operatingFluidTypes}`,
        getOperatingFluidTypes: () => `${servicePath}/${operatingFluidTypes}`,
        getSheetsWithSheetTypeAndOperatingFluidFilter: (sheetType: string, filter: string[]) =>
            `${servicePath}/${sheets}?sheetType=${sheetType}&operatingFluidTypes=${filter}`,
        getSheetsWithSheetType: (sheetType: string) => `${servicePath}/${sheets}?sheetType=${sheetType}`,
        getSheetsWithSheetTypeAndSearch: (sheetType: string, search?: string) =>
            `${servicePath}/${sheets}?sheetType=${sheetType}${search ? `&search=${search}` : ''}`,
        uploadNewOperatingFluidType: () => `${servicePath}/${operatingFluidTypes}`,
        updateOperatingFluidType: (operatingFluidTypeId: string) =>
            `${servicePath}/${operatingFluidTypes}/${operatingFluidTypeId}`,
        operatingFluidTypeById: (operatingFluidTypeId: string) =>
            `${servicePath}/${operatingFluidTypes}/${operatingFluidTypeId}`,
        saeClasses: `${servicePath}/${saeClasses}`,
        getSaeClasses: () => `${servicePath}/${saeClasses}`,
        uploadNewSaeClass: () => `${servicePath}/${saeClasses}`,
        getSaeClassesByFilterSelect: (queryString: string) => `${servicePath}/${saeClasses}?${queryString}`,
        updateSaeClass: (saeClassId: string) => `${servicePath}/${saeClasses}/${saeClassId}`,
        saeClassById: (saeClassId: string) => `${servicePath}/${saeClasses}/${saeClassId}`,
        mbSpecifications: `${servicePath}/${mbSpecifications}`,
        getMbSpecifications: (operatingFluidTypeId?: string) => {
            if (!operatingFluidTypeId) {
                return `${servicePath}/${mbSpecifications}`;
            }
            return `${servicePath}/${mbSpecifications}?operatingFluidTypeId=${operatingFluidTypeId}`;
        },
        uploadNewMbSpecification: (copyFrom?: string) =>
            `${servicePath}/${mbSpecifications}${copyFrom ? `?copyFrom=${copyFrom}` : ''}`,
        updateMbSpecification: (mbSpecificationsId: string) =>
            `${servicePath}/${mbSpecifications}/${mbSpecificationsId}`,
        mbSpecificationById: (mbSpecificationsId: string) => `${servicePath}/${mbSpecifications}/${mbSpecificationsId}`,
        qualityLines: `${servicePath}/${qualityLines}`,
        getQualityLines: () => `${servicePath}/${qualityLines}`,
        uploadNewQualityLine: () => `${servicePath}/${qualityLines}`,
        updateQualityLine: (qualityLineId: string) => `${servicePath}/${qualityLines}/${qualityLineId}`,
        qualityLineById: (qualityLineId: string) => `${servicePath}/${qualityLines}/${qualityLineId}`,
        getSalesAreas: (search?: string) =>
            search ? `${servicePath}/${salesAreas}?search=${search}` : `${servicePath}/${salesAreas}`,
        uploadNewSalesArea: () => `${servicePath}/${salesAreas}`,
        updateSalesArea: (salesAreaId: string) => `${servicePath}/${salesAreas}/${salesAreaId}`,
        deleteSalesArea: (salesAreaId: string) => `${servicePath}/${salesAreas}/${salesAreaId}`,
        salesAreaById: (salesAreaId: string) => `${servicePath}/${salesAreas}/${salesAreaId}`,
        getInfolettersForCompany: (registrationId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${infolettersPrefix}`,
        getInfoletterForCompanyById: (registrationId: string, infoletterId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${infolettersPrefix}/${infoletterId}`,
        downloadInfoletterForCompanyById: (registrationId: string, infoletterId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${infolettersPrefix}/${infoletterId}`,
        getWorkingMaterials: (search?: string) =>
            search
                ? `${servicePath}/${workingMaterials}?majorAssemblyModel=${search}`
                : `${servicePath}/${workingMaterials}`,
        uploadWorkingMaterialsFile: (fileType: string) => `${servicePath}/${workingMaterials}?fileType=${fileType}`,
        uploadWorkingMaterial: (fileType: string) => `${servicePath}/${workingMaterials}?fileType=${fileType}`,
        updateWorkingMaterial: (workingMaterialId: string) => `${servicePath}/${workingMaterials}/${workingMaterialId}`,
        deleteWorkingMaterial: (workingMaterialId: string) => `${servicePath}/${workingMaterials}/${workingMaterialId}`,
        workingMaterialTemplates: `${servicePath}/${workingMaterialTemplates}`,
        getWorkingMaterialTemplates: () => `${servicePath}/${workingMaterialTemplates}`,
        baseOils: `${servicePath}/${baseOils}`,
        getBaseOils: () => `${servicePath}/${baseOils}`,
        getBaseOilsWithSearch: (search: string) => `${servicePath}/${baseOils}?search=${search}`,
        getBaseOilsByFilterModelFilterAndSearch: (queryString: string, search: string) =>
            `${servicePath}/${baseOils}?${queryString}&search=${search}`,
        getBaseOilsByFilterSelect: (queryString: string) => `${servicePath}/${baseOils}?${queryString}`,
        baseOilById: (baseOilId: string) => `${servicePath}/${baseOils}/${baseOilId}`,
        uploadNewBaseOil: () => `${servicePath}/${baseOils}`,
        updateBaseOil: (baseOilId: string) => `${servicePath}/${baseOils}/${baseOilId}`,
        deleteBaseOil: (baseOilId: string) => `${servicePath}/${baseOils}/${baseOilId}`,
        baseOilFilesById: (baseOilId: string) => `${servicePath}/${baseOils}/${baseOilId}/files`,
        baseOilFileById: (baseOilId: string, fileId: string) =>
            `${servicePath}/${baseOils}/${baseOilId}/files/${fileId}`,
        uploadFileByBaseOilId: (baseOilId: string) => `${servicePath}/${baseOils}/${baseOilId}/files`,
        deleteBaseOilFileById: (baseOilId: string, fileId: string) =>
            `${servicePath}/${baseOils}/${baseOilId}/files/${fileId}`,
        getAdditives: () => `${servicePath}/${additives}`,
        getAdditivesWithSearch: (search: string) => `${servicePath}/${additives}?search=${search}`,
        getAdditivesByFilterModelFilterAndSearch: (queryString: string, search: string) =>
            `${servicePath}/${additives}?${queryString}&search=${search}`,
        getAdditivesByFilterSelect: (queryString: string) => `${servicePath}/${additives}?${queryString}`,
        additiveById: (additiveId: string) => `${servicePath}/${additives}/${additiveId}`,
        uploadNewAdditive: () => `${servicePath}/${additives}`,
        updateAdditive: (additiveId: string) => `${servicePath}/${additives}/${additiveId}`,
        deleteAdditive: (additiveId: string) => `${servicePath}/${additives}/${additiveId}`,
        additiveFilesById: (additiveId: string) => `${servicePath}/${additives}/${additiveId}/files`,
        additiveFileById: (additiveId: string, fileId: string) =>
            `${servicePath}/${additives}/${additiveId}/files/${fileId}`,
        uploadFileByAdditiveId: (additiveId: string) => `${servicePath}/${additives}/${additiveId}/files`,
        deleteAdditiveFileById: (additiveId: string, fileId: string) =>
            `${servicePath}/${additives}/${additiveId}/files/${fileId}`,
        news: `${servicePath}/${news}`,
        getNews: (pagination: Pageable) =>
            `${servicePath}/${news}?page=${pagination.page}&size=${pagination.size}&sort=${pagination.sort?.join(
                '&sort='
            )}`,
        getNewsByNewsState: (pagination: Pageable, newsState: string) =>
            `${servicePath}/${news}?newsState=${newsState}&page=${pagination.page}&size=${pagination.size
            }&sort=${pagination.sort?.join('&sort=')}`,
        uploadNewNews: () => `${servicePath}/${news}`,
        updateNews: (newsId: string) => `${servicePath}/${news}/${newsId}`,
        deleteNews: (newsId: string) => `${servicePath}/${news}/${newsId}`,
        uploadFileByNewsId: (newsId: string, lang: string) =>
            `${servicePath}/${news}/${newsId}/files?fileLanguage=${lang}`,
        deleteNewsFileById: (newsId: string, fileId: string) => `${servicePath}/${news}/${newsId}/files/${fileId}`,
        getEngineTypes: () => `${servicePath}/${engineTypes}`,
        getEngineTypeById: (engineTypeId: string) => `${servicePath}/${engineTypes}/${engineTypeId}`,
        uploadNewEngineType: () => `${servicePath}/${engineTypes}`,
        deleteEngineTypeById: (engineTypeId: string) => `${servicePath}/${engineTypes}/${engineTypeId}`,
        updateEngineTypeById: (engineTypeId: string) => `${servicePath}/${engineTypes}/${engineTypeId}`,
        getFuels: () => `${servicePath}/${fuels}`,
        getFuelById: (fuelId: string) => `${servicePath}/${fuels}/${fuelId}`,
        uploadNewFuel: () => `${servicePath}/${fuels}`,
        deleteFuelById: (fuelId: string) => `${servicePath}/${fuels}/${fuelId}`,
        updateFuelById: (fuelId: string) => `${servicePath}/${fuels}/${fuelId}`,
        getInvoiceAmount: () => `${servicePath}/${invoiceAmount}`,
        getInvoiceAmountById: (invoiceId: string) => `${servicePath}/${invoiceAmount}/${invoiceId}`,
        uploadNewInvoiceAmount: () => `${servicePath}/${invoiceAmount}`,
        deleteInvoiceAmountById: (invoiceId: string) => `${servicePath}/${invoiceAmount}/${invoiceId}`,
        updateInvoiceAmountById: (invoiceId: string) => `${servicePath}/${invoiceAmount}/${invoiceId}`,
        getTestStands: () => `${servicePath}/${programTestBenches}`,
        getTestStandById: (testStandId: string) => `${servicePath}/${programTestBenches}/${testStandId}`,
        uploadNewTestStand: () => `${servicePath}/${programTestBenches}`,
        deleteTestStandById: (testStandId: string) => `${servicePath}/${programTestBenches}/${testStandId}`,
        updateTestStandById: (testStandId: string) => `${servicePath}/${programTestBenches}/${testStandId}`,
        getTestBenchResults: () => `${servicePath}/${testBenchResults}`,
        getTestBenchResultById: (testResultId: string) => `${servicePath}/${testBenchResults}/${testResultId}`,
        uploadNewTestBenchResult: () => `${servicePath}/${testBenchResults}`,
        deleteTestBenchResultById: (testResultId: string) => `${servicePath}/${testBenchResults}/${testResultId}`,
        updateTestBenchResultById: (testResultId: string) => `${servicePath}/${testBenchResults}/${testResultId}`,
        serviceAreas: `${servicePath}/${serviceAreas}`,
        getServiceAreas: () => `${servicePath}/${serviceAreas}`,

        getServiceAreasSortedBy: (sort: string[]) =>
            `${servicePath}/${serviceAreas}?&sort=${sort?.join(',')}&${maxPaginationSettingString}`,
        uploadNewServiceArea: () => `${servicePath}/${serviceAreas}`,
        getServiceAreaById: (serviceAreaId: string) => `${servicePath}/${serviceAreas}/${serviceAreaId}`,
        deleteServiceAreaById: (serviceAreaId: string) => `${servicePath}/${serviceAreas}/${serviceAreaId}`,
        updateServiceAreaById: (serviceAreaId: string) => `${servicePath}/${serviceAreas}/${serviceAreaId}`,
        uploadFileByServiceAreaId: (serviceAreaId: string, lang: string) =>
            `${servicePath}/${serviceAreas}/${serviceAreaId}/files?fileLanguage=${lang}`,
        getServiceAreaFilesById: (serviceAreaId: string) => `${servicePath}/${serviceAreas}/${serviceAreaId}/files`,
        deleteServiceAreaFileById: (serviceAreaId: string, fileId: string) =>
            `${servicePath}/${serviceAreas}/${serviceAreaId}/files/${fileId}`,
        downloadServiceAreaFileById: (serviceAreaId: string, fileId: string) =>
            `${servicePath}/${serviceAreas}/${serviceAreaId}/files/${fileId}/content`,
        getFAQs: () => `${servicePath}/${frequentlyAskedQuestions}`,
        updateFAQById: (FAQIdId: string) => `${servicePath}/${frequentlyAskedQuestions}/${FAQIdId}`,
        deleteFAQById: (FAQIdId: string) => `${servicePath}/${frequentlyAskedQuestions}/${FAQIdId}`,
        uploadNewFAQ: () => `${servicePath}/${frequentlyAskedQuestions}`,
        getFAQById: (FAQIdId: string) => `${servicePath}/${frequentlyAskedQuestions}/${FAQIdId}`,
        uploadFileByFAQId: (FAQIdId: string, lang: string) =>
            `${servicePath}/${frequentlyAskedQuestions}/${FAQIdId}/files?fileLanguage=${lang}`,
        getFAQFilesById: (FAQIdId: string) => `${servicePath}/${frequentlyAskedQuestions}/${FAQIdId}/files`,
        deleteFAQFileById: (FAQIdId: string, fileId: string) =>
            `${servicePath}/${frequentlyAskedQuestions}/${FAQIdId}/files/${fileId}`,
        downloadFAQFileById: (FAQIdId: string, fileId: string) =>
            `${servicePath}/${frequentlyAskedQuestions}/${FAQIdId}/files/${fileId}/content`,
        dblCategories: `${servicePath}/${dblCategories}`,
        getDblCategories: () => `${servicePath}/${dblCategories}?${maxPaginationSettingString}`,
        getDblCategory: (dblCategoryId: string) => `${servicePath}/${dblCategories}/${dblCategoryId}`,
        uploadDblCategory: () => `${servicePath}/${dblCategories}`,
        deleteDblCategoryById: (dblCategoryId: string) => `${servicePath}/${dblCategories}/${dblCategoryId}`,
        updateDblCategoryById: (dblCategoryId: string) => `${servicePath}/${dblCategories}/${dblCategoryId}`,
        dblSpecifications: `${servicePath}/${dblSpecifications}`,
        getDblSpecificationsByCatagoryId: (dblCategoryId: string) =>
            `${servicePath}/${dblCategories}/${dblCategoryId}/${dblSpecifications}?${maxPaginationSettingString}`,
        getDblSpecification: (dblCategoryId: string, dblSpecificationId: string) =>
            `${servicePath}/${dblCategories}/${dblCategoryId}/${dblSpecifications}/${dblSpecificationId}`,
        uploadDblSpecifications: (dblCategoryId: string) =>
            `${servicePath}/${dblCategories}/${dblCategoryId}/${dblSpecifications}`,
        deleteDblSpecificationById: (dblCategoryId: string, dblSpecificationId: string) =>
            `${servicePath}/${dblCategories}/${dblCategoryId}/${dblSpecifications}/${dblSpecificationId}`,
        updateDblSpecificationById: (dblCategoryId: string, dblSpecificationId: string) =>
            `${servicePath}/${dblCategories}/${dblCategoryId}/${dblSpecifications}/${dblSpecificationId}`,
        thirdParties: `${servicePath}/${thirdParties}`,
        getThirdParties: (companyId: string) => `${servicePath}/${thirdParties}?companyId=${companyId}`,
        uploadThirdParty: () => `${servicePath}/${thirdParties}`,
        updateThirdParty: (thirdPartyId: string) => `${servicePath}/${thirdParties}/${thirdPartyId}`,
        uploadFileByThirdPartyId: (thirdPartyId: string, fileType: ThirdPartyContractFileType) =>
            `${servicePath}/${thirdParties}/${thirdPartyId}/files?fileType=${fileType}`,
        getFilesByThirdPartyId: (thirdPartyId: string) => `${servicePath}/${thirdParties}/${thirdPartyId}/files`,
        deleteThirdPartyFileById: (thirdPartyId: string, filesId: string) =>
            `${servicePath}/${thirdParties}/${thirdPartyId}/files/${filesId}`,
        downloadThirdPartyFileById: (thirdPartyId: string, fileId: string) =>
            `${servicePath}/${thirdParties}/${thirdPartyId}/files/${fileId}/content`,
        containerSize: `${servicePath}/${containerSize}`,
        getContainerSizes: (search?: string) =>
            search ? `${servicePath}/${containerSize}?search=${search}` : `${servicePath}/${containerSize}`,
        uploadNewContainerSize: () => `${servicePath}/${containerSize}`,
        updateContainerSize: (containerSizeId: string) => `${servicePath}/${containerSize}/${containerSizeId}`,
        deleteContainerSize: (containerSizeId: string) => `${servicePath}/${containerSize}/${containerSizeId}`,
        containerSizeById: (containerSizeId: string) => `${servicePath}/${containerSize}/${containerSizeId}`,
        vehicles: `${servicePath}/${vehicles}`,
        getVehicles: () => `${servicePath}/${vehicles}`,
        uploadVehiclesFile: () => `${servicePath}/${vehicles}`,
        getSheetsByFilterModelFilterAndSearch: (queryString: string, search: string) =>
            `${servicePath}/${sheets}?${queryString}&search=${search}`,
        invoices: `${servicePath}/${invoices}`,
        uploadFileByInvoiceId: (invoiceId: string, fileType: InvoiceFileType) =>
            `${servicePath}/${invoices}/${invoiceId}/files?fileType=${fileType}`,
        getInvoiceFileByIdContent: (invoiceId: string, fileId: string) =>
            `${servicePath}/${invoices}/${invoiceId}/files/${fileId}/content`,
        getInvoices: () => `${servicePath}/${invoices}`,
        getInvoicesWithSearch: (search: string) => `${servicePath}/${invoices}?search=${encodeURIComponent(search.trim())}`,
        getInvoicesByFilterModelFilterAndSearch: (queryString: string, search: string) =>
            `${servicePath}/${invoices}?${queryString}&search=${encodeURIComponent(search.trim())}`,
        getInvoicesByFilterSelect: (queryString: string) => `${servicePath}/${invoices}?${queryString}`,
        getInvoiceFileById: (invoiceId: string) => `${servicePath}/${invoices}/${invoiceId}/files`,
        deleteInvoiceFileById: (invoiceId: string, fileId: string) =>
            `${servicePath}/${invoices}/${invoiceId}/files/${fileId}`,
        getInvoicesByCompanyId: (companyId: string) => `${servicePath}/${invoices}/${company}/${companyId}`,
        updateNewInvoice: (invoiceId: string) => `${servicePath}/${invoices}/${invoiceId}`,
        getInvoiceById: (invoiceId: string) => `${servicePath}/${invoices}/${invoiceId}`,
        getAllTestStructuresSortedBy: (
            operatingFluidTypeId: string,
            testAreaType: GenericTestAreaDto.testAreaType,
            appendTestStructureTree?: boolean
        ) =>
            `${servicePath}/${testAreas}?${operatingFluidTypeIdParam}=${operatingFluidTypeId}&${testAreasType}=${testAreaType}&${appendTestTree}=${appendTestStructureTree}`,
        getTestAreasByLetterFromMbAvailable: (operatingFluidTypeId: string) =>
            `${servicePath}/${testAreas}?${letterFromMbAvailableParam}=true&${operatingFluidTypeIdParam}=${operatingFluidTypeId}&${appendTestTree}=true`,
        getAllMbSpecifications: () => `${servicePath}/${mbSpecifications}`,
        getProductById: (productId: string) => `${servicePath}/${products}/${productId}`,
        updateProductById: (productId: string) => `${servicePath}/${products}/${productId}`,
        getProductFormulations: createGetProductFormulations(servicePath),
        getProductFormulationFiles: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/files`,
        getProductFormulationFile: (productId: string, formulationId: string, fileId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/files/${fileId}/content`,
        getProductFormulationById: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}`,
        updateProductFormulationById: (productId: string, formulationId: string, setDraftMode?: boolean) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}${setDraftMode === undefined ? '' : `?setDraftMode=${setDraftMode}`
            }`,
        getProductSheets: (productId: string) => `${servicePath}/${products}/${productId}/sheets`,
        getProducts: () => `${servicePath}/${products}`,
        getProductsWithSearch: (search: string) => `${servicePath}/${products}?search=${encodeURIComponent(search.trim())}`,
        getProductsFilteredByManufacturer: (id: string, options: ProductApiFilters) => {
            const params = new URLSearchParams();

            params.append(`${manufacturerId}`, id);

            if (options.onlyApproved) {
                params.append(`${onlyApprovedParam}`, 'true');
            }

            if (options.onlyTechnicallyApproved) {
                params.append(`${onlyTechnicallyApprovedParam}`, 'true');
            }

            const queryString = params.toString();

            return `${servicePath}/${products}?${queryString}`;
        },
        getProductApprovals: () => `${servicePath}/${productApprovals}`,
        getProductApprovalsWithState: (state: string) => `${servicePath}/${productApprovals}?${state}`,
        getProductApprovalsWithSearch: (search: string) => `${servicePath}/${productApprovals}?search=${encodeURIComponent(search.trim())}`,
        getProductApprovalsWithStateAndSearch: (state: string, search: string) =>
            `${servicePath}/${productApprovals}?search=${encodeURIComponent(search.trim())}&${state}`,
        getProductApprovalById: (productApprovalId: string) =>
            `${servicePath}/${productApprovals}/${productApprovalId}`,
        getProductApprovalCheckStepsAdmin: (productApprovalId: string) =>
            `${servicePath}/${productApprovals}/${productApprovalId}/${checkSteps}`,
        updateProductApprovalCheckStep: (productApprovalId: string, checkStepId: string) =>
            `${servicePath}/${productApprovals}/${productApprovalId}/${checkSteps}/${checkStepId}`,
        updateProductApprovalCheckStepFile: (
            productApprovalId: string,
            checkStepId: string,
            checkStepFileType: ProcessDetailCheckStepFileType
        ) =>
            `${servicePath}/${productApprovals}/${productApprovalId}/${checkSteps}/${checkStepId}/files?${checkStepFileTypeQuery}=${checkStepFileType}`,
        getProductApprovalCheckStepFiles: (productApprovalId: string, checkStepId: string) =>
            `${servicePath}/${productApprovals}/${productApprovalId}/${checkSteps}/${checkStepId}/files`,
        getProductApprovalCheckStepFileById: (productApprovalId: string, checkStepId: string, fileId: string) =>
            `${servicePath}/${productApprovals}/${productApprovalId}/${checkSteps}/${checkStepId}/files/${fileId}/content`,
        updateProductApprovalState: (productApprovalId: string, skipValidations?: boolean) => {
            if (skipValidations) {
                return `${servicePath}/${productApprovals}/${productApprovalId}?skipValidations=${skipValidations}`;
            }
            return `${servicePath}/${productApprovals}/${productApprovalId}`;
        },
        rejectProductApprovalWithDependents: (productApprovalId: string) =>
            `${servicePath}/${productApprovals}/${productApprovalId}/reject-with-dependents`,
        getProductApprovalsByProductId: (productId: string, size?: number) => {
            const sizeParam = size ? `&size=${size}` : '';
            return `${servicePath}/${productApprovals}?productId=${productId}${sizeParam}`;
        },
        getProductFormulationTestProgress: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/${testProgress}`,
        getProductFormulationTestStepValues: (
            productId: string,
            formulationId: string,
            testGroupId: string,
            letterNumber?: string
        ) => {
            const letterNumberParamString = letterNumber ? `&${letterNumberParam}=${letterNumber}` : '';
            return `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/${testValues}?${testGroupIdParam}=${testGroupId}&${maxPaginationSettingString}${letterNumberParamString}`;
        },
        getAdditiveBaseOilTestStepValues: (
            productId: string,
            formulationId: string,
            valueType:
                | TestStepValueWrapper.testStepValueWrapperType.ADDITIVE
                | TestStepValueWrapper.testStepValueWrapperType.BASE_OIL
        ) => {
            return `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/${testValues}?valueType=${valueType}`;
        },
        activeProductFormulation: (productId: string, productFormulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${productFormulationId}`,

        getLegacyCompanies: () => `${servicePath}/${registrationsPrefix}?${legacyCompanyParam}=${true}`,
        deleteFileByIdForRegistrationById: (registrationId: string, presentationId: string) =>
            `${servicePath}/${registrationsPrefix}/${registrationId}/${companyPresentationPrefix}/${presentationId}`,
        getProductFormulationAnnualReport: (productId: string, formulationId: string) =>
            `${servicePath}/${products}/${productId}/${formulations}/${formulationId}/annual-reports`,
        getMasterDataSubstanceTypes: (search?: string) => {
            return search
                ? `${servicePath}/${substanceTypesParam}?search=${search}`
                : `${servicePath}/${substanceTypesParam}`;
        },
        getMasterDataSubstanceTypeById: (substanceTypeId: string) =>
            `${servicePath}/${substanceTypesParam}/${substanceTypeId}`,
        deleteMasterDataSubstanceTypeById: (substanceTypeId: string) =>
            `${servicePath}/${substanceTypesParam}/${substanceTypeId}`,
        updateMasterDataSubstanceType: (substanceTypeId: string) =>
            `${servicePath}/${substanceTypesParam}/${substanceTypeId}`,
        createMasterDataSubstanceType: () => `${servicePath}/${substanceTypesParam}`,
    };
}

function getPublicApiUrls(baseUrl: string) {
    const servicePath = `${baseUrl}`;

    return {
        getPublicFile: (publicFile: PublicFiles, lang: string) =>
            `${servicePath}/${publicFiles}?type=${publicFile}&fileLanguage=${lang}`,
    } as ApiUrlTask;
}

/**
 * Central api URL access function
 *
 * @export
 * @return {*}
 */
export const ApiUrls = {
    userApi: getUserApiUrls(endpointConfig.env.SERVICES_BASE_URL, 'v1'),
    adminApi: getAdminApiUrls(endpointConfig.env.SERVICES_BASE_URL, 'v1'),
    publicApi: getPublicApiUrls(endpointConfig.env.SERVICES_BASE_URL),
};
